/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import docApi from "api";
import { each } from "utils/lodashUtils";
import {
  getTotalPages,
  dialogOpened,
  isFilePasswordProtected,
  isDocumentEncrypted,
  getNeedAppearancesStatus,
  getShouldTriggerCalculate,
  getFieldCalculationOrder
} from "selectors/documentSelectors";
import * as constants from "constants/constants";
import { setFixedWidthMode, setShowCommentsPanel, transformRectViewToPDF } from "utils/PageUtils";
import {
  transformPDFToView,
  transformViewToPDF,
  calculateRectRelativeToPageRect
} from "dc-preview-utils/src/Utils/PageUtils";
import { getPageZoomLevel } from "selectors/zoom";
import { subscribeToSelector } from "dc-preview-utils/src/Utils/MobXUtils";
import { isInitialPageLoaded, isFirstPageLoaded } from "selectors/pagedViewState";
import { noPreviewErrorCodes } from "constants/enums";
import { contextMenuTriggerType } from "dc-preview-utils/src/Constants/enums";
import { setNewLocation, initJSEngine } from "pdfActionManager";
import { apiNames } from "constants/apiNames";
import { pageContentHandler } from "pageContentHandler";
import { contextMenuManager } from "contextMenuManager";
import { onDownloadFileClick } from "components/topbar/actions/downloadFile";
import { pagesInView } from "selectors/pagedViewSelectors";
import {
  getPageHeight,
  getPageMountStatus,
  getAcroformAppearance,
  unloadAppearances,
  getPageHandle
} from "selectors/pageSelectors";
import { isPointInsideCanvasQuadUnion } from "dc-preview-utils/src/Utils/SelectionUtils";
import throwJSError from "javascript/jsEngine/utils/jsError";
import threadHostSimulator from "javascript/jsEngine/threadHostSimulator";
import {
  isAllowed,
  hasSecurityBitSet,
  getDocumentSecurityInformation,
  isCommentingAllowed,
  isFormFillingAllowed
} from "utils/DocPermissionsUtils";
import { getCurrentPageNumber, getCurrentPageIndex } from "selectors/pageNumber";
import { getCoordinatesRelativeToTargetElem, getCoordinatesRelativeToPage } from "utils/EventHandlingUtils";

import {
  wrappedSearchContainer,
  wrappedDownloadButtonContainer,
  wrappedPrintButtonContainer
} from "components/topbar/reusableComponents";
import {
  isReadOnlyModeEnabled,
  getKillSwitchJSOverridePromise,
  getSupportPDFJavaScriptPromise,
  getSupportPDFJavaScript,
  getIsDisabledAcroformValidationFormatting
} from "selectors/appSelectors";
import { getContextMenuTriggerType } from "../selectors/contextMenuSelectors";
import { isPersonalCommentingEnabled } from "utils/FeatureFlagUtils";
import commandManager from "undoRedo/CommandManager";
import { isIntegrationContext, rectsIntersection } from "utils/common";
import { getDocumentId } from "../analytics/interface";
import { getPagesViewBoundingRect, getNoPreviewErrorCode } from "selectors/documentViewSelectors";
import { getAcroformDropinMountedPromise, getShouldEnableEncryptedWrite } from "selectors/overlayDropinSelectors";
import { executeFormsNonJSDocActions } from "pdfActionManager/docActionsHelper";
import { getPageOverlayApis } from "../selectors/overlayDropinSelectors";
import { trackDocAndFormJSActions } from "utils/pdfActionUtils";
import { showUnsupportedWidgetNotification } from "utils/BannerUtils";
import { getZoomedQuadsObj } from "utils/SelectionUtils";
import { isThereAnySelection } from "dc-preview-utils/src/selectors/touchSelectors";
import { getPageQuadsArray } from "dc-preview-utils/src/selectors/pageHighlightsSelectors";

const getJsApis = stores => ({
  throwJSError,
  initializeJSEngine: async () => {
    let jsEngine;
    try {
      jsEngine = await initJSEngine(stores);
    } catch (err) {
      console.log("unable to initialize js engine", err);
    }
    return jsEngine;
  },
  initContexts: params => {
    stores.appStore.setJSEngineInitContextsParams(params);
  },
  postToWorkerAndWaitBWL: threadHostSimulator.postToWorkerAndWait,
  setIsDropinMounted: dropinName => {
    if (dropinName === "acroform") {
      getAcroformDropinMountedPromise(stores).resolve();
    }
  },
  getDocProperties: () => ({
    shouldTriggerCalculate: getShouldTriggerCalculate(stores),
    needAppearancesStatus: getNeedAppearancesStatus(stores)
  }),
  getFieldCalculationOrder: () => getFieldCalculationOrder(stores),
  getKillSwitchJSOverridePromise: () => getKillSwitchJSOverridePromise(stores),
  getSupportPDFJavaScriptPromise: () => getSupportPDFJavaScriptPromise(stores),
  getSupportPDFJavaScript: () => getSupportPDFJavaScript(stores)
});

export const commentsApi = stores => {
  const apiName = apiNames.COMMENTS;
  const apiGetter = () => ({
    onCommentsReady: onCommentsReady => {
      subscribeToSelector(stores, isInitialPageLoaded, () => {
        onCommentsReady(isInitialPageLoaded(stores));
      });
    },
    getCommentsList: (...args) => docApi.getCommentsList(...args),
    createComment: (...args) => docApi.createComment(...args),
    editComment: (...args) => docApi.editComment(...args),
    deleteComment: (...args) => docApi.deleteComment(...args),
    getCommentAppearance: (...args) => docApi.getCommentAppearance(...args),
    getVoiceNoteData: (...args) => docApi.getVoiceNoteData(...args),
    createComments: (...args) => docApi.createComments(...args),
    deleteComments: (...args) => docApi.deleteComments(...args),
    jsApis: getJsApis(stores)
  });

  return {
    apiName,
    apiGetter
  };
};

const updateDataInModule = (id, data, stores) => {
  const overlayApis = getPageOverlayApis(stores, "acroform");
  if (!overlayApis) {
    return;
  }
  overlayApis.updateField({ id }, data, {
    updateIn: constants.UPDATE_IN.FORMS_MODULE | constants.UPDATE_IN.DROPIN
  });
};

export const acroformApi = stores => {
  const apiName = apiNames.ACROFORM;
  const apiGetter = () => ({
    getAcroFormFields: (...args) => docApi.getAcroFormFields(...args),
    editAcroFormField: (...args) => {
      const response = docApi.editAcroFormField(...args);
      const [_pageIndex, id, data, shouldUpdateModule = true] = args;
      if (shouldUpdateModule) {
        updateDataInModule(id, data, stores);
      }
      return response;
    },
    editAcroFormFields: (...args) => {
      const [fields = [], shouldUpdateModule = true] = args;
      if (shouldUpdateModule) {
        fields.forEach(({ id, data }) => {
          updateDataInModule(id, data, stores);
        });
      }
      return docApi.editAcroFormFields(...args);
    },
    createAcroFormField: (...args) => {
      const resp = docApi.createAcroFormField(...args);
      resp.then(response => {
        const [pageIndex, fieldData, shouldUpdateModule = true] = args;
        if (shouldUpdateModule && response.data && response.data.id) {
          const overlayApis = getPageOverlayApis(stores, "acroform");
          const data = { ...fieldData, id: response.data.id };
          if (overlayApis) {
            if (fieldData.fromFieldId) {
              overlayApis.cloneField(pageIndex, data, response.data.id);
            } else {
              overlayApis.createField(pageIndex, data);
            }
          }
        }
      });
      return resp;
    },
    createAcroFormFields: (...args) => {
      const resp = docApi.createAcroFormFields(...args);
      resp.then(response => {
        const [fields = [], shouldUpdateModule = true] = args;
        if (shouldUpdateModule && response.data && response.data.length === fields.length) {
          const overlayApis = getPageOverlayApis(stores, "acroform");
          if (overlayApis) {
            fields.forEach(({ data, pageNum }, index) => {
              if (response.data[index].data && response.data[index].data.id) {
                data = { ...data, id: response.data[index].data.id };
                if (data.fromFieldId) {
                  overlayApis.cloneField(pageNum, data, response.data[index].data.id);
                } else {
                  overlayApis.createField(pageNum, data);
                }
              }
            });
          }
        }
      });
      return resp;
    },
    deleteAcroFormField: (...args) => {
      const response = docApi.deleteAcroFormField(...args);
      const [pageIndex, id, shouldUpdateModule = true] = args;
      if (shouldUpdateModule) {
        const overlayApis = getPageOverlayApis(stores, "acroform");
        if (overlayApis) {
          overlayApis.removeField(id, pageIndex);
        }
      }
      return response;
    },
    deleteAcroFormFields: (...args) => {
      const [fields = [], shouldUpdateModule = true] = args;
      if (shouldUpdateModule) {
        const overlayApis = getPageOverlayApis(stores, "acroform");
        if (overlayApis) {
          fields.forEach(({ id, pageNum }) => {
            overlayApis.removeField(id, pageNum);
          });
        }
      }
      return docApi.deleteAcroFormFields(...args);
    },
    updateSignMetadata: (...args) => docApi.updateSignMetadata(...args),
    getSignMetadata: (...args) => docApi.getSignMetadata(...args),
    getCommentAppearance: (...args) => getAcroformAppearance(stores, ...args),
    unloadAppearances: (...args) => unloadAppearances(stores, ...args),
    getContentBuffer: (...args) => docApi.getContentBuffer(...args),
    executeFormsNonJSDocActions: () => executeFormsNonJSDocActions(stores),
    isFormFillingAllowed: () => isFormFillingAllowed(),
    jsApis: getJsApis(stores),
    trackJSActionHelper: (...args) => trackDocAndFormJSActions(...args),
    shouldDisableValidationFormatting: () => getIsDisabledAcroformValidationFormatting(stores)
  });

  return {
    apiName,
    apiGetter
  };
};

export const reusableComponentsApi = () => {
  const apiName = apiNames.REUSABLE_COMPONENTS;
  const apiGetter = () => ({
    getSearchUI: () => wrappedSearchContainer,
    getDownloadButton: () => wrappedDownloadButtonContainer,
    getPrintButton: () => wrappedPrintButtonContainer
  });

  return {
    apiName,
    apiGetter
  };
};

export const documentActionApis = stores => {
  const apiName = apiNames.DOC_ACTIONS;
  const apiGetter = () => ({
    goToLocation: ({ pageIndex, left, top }) => setNewLocation(stores, pageIndex, left, top),
    setFixedWidthMode: fixedWidthMode => setFixedWidthMode(fixedWidthMode, stores),
    setShowCommentsPanel: val => setShowCommentsPanel(val, stores),
    clearPageSelection: (...args) => {
      pageContentHandler.clearPageRects(stores, ...args);
    },
    copySelectedText: () => {
      pageContentHandler.getTextManagerInstance().copyText();
    },
    downloadDocument: () => {
      onDownloadFileClick(stores, {
        triggeredFrom: "api"
      });
    },
    getStartingCoords: args =>
      docApi.textSelectionFromPDFCoordinates(
        args.startPoint,
        args.endPoint,
        args.priority,
        args.snapToWords,
        args.getCaret,
        args.force
      ),
    notifyPageOverflownByOverlay: (pageIndex, amount) => {
      const totalPages = getTotalPages(stores);
      if (pageIndex === totalPages - 1) {
        stores.documentViewStore.setLastPageExtraBottomSpace(amount);
      }
    },
    showUnsupportedWidgetNotification: (...params) => {
      showUnsupportedWidgetNotification(...params);
    }
  });

  return {
    apiName,
    apiGetter
  };
};

export const pageViewApis = stores => {
  const apiName = apiNames.PAGE_VIEW;
  const apiGetter = () => ({
    transformPDFToView: (x, y, pageIndex, zoomLevel, options = {}) =>
      transformPDFToView(x, y, pageIndex, stores, zoomLevel, options),
    transformViewToPDF: (x, y, pageIndex, options = {}) =>
      transformViewToPDF(x, y, pageIndex, stores, getPageZoomLevel(stores, pageIndex), options),
    loadPage: pageIndex => {
      stores.documentStore.unloadPage(pageIndex);
      stores.documentStore.loadPage(
        pageIndex,
        getPageZoomLevel(stores, pageIndex),
        pagesInView(stores),
        constants.PAGE_PRIORITY
      );
    },
    getZoomedQuadsObj: (quadsArrayInPDFCoords, shouldTransformToViewQuads = true) =>
      getZoomedQuadsObj(quadsArrayInPDFCoords, stores, shouldTransformToViewQuads)
  });

  return {
    apiName,
    apiGetter
  };
};

export const modifyCursorApis = stores => {
  const apiName = apiNames.MODIFY_CURSOR;
  const apiGetter = () => ({
    setCursor: args => {
      stores.overlayDropinStore.cursorStateChange(true);
      stores.overlayDropinStore.setCurrentCursor(args);
    },
    resetCursor: () => {
      stores.overlayDropinStore.cursorStateChange(false);
      stores.overlayDropinStore.setCurrentCursor("default");
    }
  });

  return {
    apiName,
    apiGetter
  };
};

export const documentStateApis = stores => {
  const apiName = apiNames.DOC_STATE;
  const apiGetter = () => ({
    getCurrentPage: () => Promise.resolve(getCurrentPageNumber(stores)),
    getPageCurrHeight: pageIndex => getPageHeight(stores, pageIndex),
    getTotalPages: () =>
      new Promise((res, rej) => {
        subscribeToSelector(stores, isInitialPageLoaded, () => {
          if (isInitialPageLoaded(stores)) {
            res(getTotalPages(stores));
          }
        });
      }),
    getPagesViewBoundingRect: () => getPagesViewBoundingRect(stores),
    getDocumentId: () => getDocumentId(),
    initialPageLoaded: () =>
      new Promise((resolve, reject) => {
        subscribeToSelector(stores, isInitialPageLoaded, () => {
          if (isInitialPageLoaded(stores)) {
            resolve();
          }
        });

        subscribeToSelector(stores, getNoPreviewErrorCode, () => {
          if (getNoPreviewErrorCode(stores) !== noPreviewErrorCodes.NO_ERROR) {
            reject({ errorCode: getNoPreviewErrorCode(stores) });
          }
        });
      }),
    firstPageLoaded: () =>
      new Promise((resolve, reject) => {
        subscribeToSelector(stores, isFirstPageLoaded, () => {
          if (isFirstPageLoaded(stores)) {
            resolve();
          }
        });

        subscribeToSelector(stores, getNoPreviewErrorCode, () => {
          if (getNoPreviewErrorCode(stores) !== noPreviewErrorCodes.NO_ERROR) {
            reject({ errorCode: getNoPreviewErrorCode(stores) });
          }
        });
      }),
    dialogOpened: () =>
      new Promise((resolve, reject) => {
        subscribeToSelector(stores, dialogOpened, () => {
          if (dialogOpened(stores)) {
            resolve();
          }
        });
      }),
    isCommentingAllowed: () => isCommentingAllowed(),
    getDocumentSecurityInformation: () => getDocumentSecurityInformation(),
    isPageMounted: pageIndex => getPageMountStatus(stores, pageIndex),
    toolClicked: (isSelected, tool) => {
      isSelected && stores.appStore.setSelectedTool(tool);
      !isSelected && stores.appStore.setSelectedTool("");
    },
    getVisiblePageRectInPDFView: curPageIndex => {
      const pagesViewBox = getPagesViewBoundingRect(stores);
      const pageRef = getPageHandle(stores, curPageIndex);
      const pageRect = pageRef.getBoundingClientRect();
      const intersectionRect = rectsIntersection(pagesViewBox, pageRect);
      const rect = calculateRectRelativeToPageRect(intersectionRect, pageRect);
      const rectViewInPDF = transformRectViewToPDF(
        {
          l: rect.left,
          r: rect.right,
          t: rect.top,
          b: rect.bottom
        },
        curPageIndex,
        stores,
        getPageZoomLevel(stores, curPageIndex)
      );
      return rectViewInPDF;
    }
  });

  return {
    apiName,
    apiGetter
  };
};

export const contextMenuApis = stores => {
  const apiName = apiNames.CONTEXT_MENU;
  const contextMenuHandlersMap = new Map();
  const apiGetter = () => ({
    registerMenuItems: menuItems => {
      for (let key in menuItems) {
        let callback = menuItems[key];
        const contextMenuHandler = (eventObj, stores, extraParams) => {
          // eslint-disable-line no-loop-func
          const pageIndex = extraParams.pageIndex;
          const { x, y } = getCoordinatesRelativeToTargetElem(eventObj);
          return callback(eventObj, pageIndex, x, y);
        };
        contextMenuManager.subscribe({
          name: key,
          contextMenuHandler,
          type: contextMenuTriggerType.RIGHT_CLICK
        });
      }
    },
    subscribe: contextMenuHandlerObj => {
      const contextMenuHandler = (eventObj, stores, extraParams) =>
        contextMenuHandlerObj.contextMenuHandler(eventObj, extraParams);
      const key = `${contextMenuHandlerObj.name}_${contextMenuHandlerObj.type}`;
      contextMenuHandlersMap.set(key, contextMenuHandler);

      contextMenuManager.subscribe({
        name: contextMenuHandlerObj.name,
        contextMenuHandler,
        type: contextMenuHandlerObj.type
      });
    },
    unsubscribe: type => {
      contextMenuManager.unsubscribe(type);
    },
    destroyContextMenu: () => {
      contextMenuManager.clearMenuItems(stores);
    },
    triggerContextMenu: (eventObj, extraParams, type) => {
      contextMenuManager.triggerContextMenu(eventObj, stores, extraParams, type);
    },
    setShouldTriggerContextMenuOnTextSelection: value => {
      stores.contextMenuStore.setShouldTriggerContextMenuOnTextSelection(value);
    },
    unregisterCallback: ({ name, type }) => {
      const eventName = `${name}_${type}`;
      const observer = contextMenuHandlersMap.get(eventName);
      if (observer) {
        contextMenuManager.unregister(type, observer);
        contextMenuHandlersMap.delete(eventName);
      }
    },
    getContextMenuType: () => getContextMenuTriggerType(stores),
    isPointOverSelection: (eventObj, pageIndex) => {
      if (pageIndex >= 0) {
        let { x, y } = getCoordinatesRelativeToPage(eventObj);
        return isPointInsideCanvasQuadUnion(stores, pageIndex, x, y);
      }
    },
    hideContextMenu: flag => stores.touchSelStore.hideContextMenu(flag),
    setSelectionHighlightColor: color => {
      const pageIndex = getCurrentPageIndex(stores);
      let quadsOnPage;
      if (isThereAnySelection(stores)) {
        quadsOnPage = getPageQuadsArray(stores, pageIndex).slice();
        quadsOnPage.forEach(quad => {
          quad.updateColor(color);
        });
        stores.pageHighlightsStore.updatePageCanvasQuads(pageIndex, quadsOnPage, true);
      }
    }
  });

  return {
    apiName,
    apiGetter
  };
};

export const disableContextMenuApis = stores => {
  const apiName = apiNames.DISABLE_CONTEXT_MENU;
  const apiGetter = () => ({
    registerClassNamesToDisableContextMenuOn: (classNames = []) => {
      each(classNames, className => {
        stores.contextMenuStore.addClassNameToDisableContextMenuOn(className);
      });
    }
  });

  return {
    apiName,
    apiGetter
  };
};

export const personalCommentingApis = stores => {
  const apiName = apiNames.PERSONAL_COMMENTING;
  const apiGetter = () => ({
    isPersonalCommenting: () => isPersonalCommentingEnabled(stores),
    isCommentingEnabled: () => {
      if (getShouldEnableEncryptedWrite(stores)) {
        return isAllowed(constants.COMMENTING_PERM) && !isReadOnlyModeEnabled(stores);
      }
      return (
        isAllowed(constants.COMMENTING_PERM) &&
        !isFilePasswordProtected(stores) &&
        !isDocumentEncrypted(stores) &&
        !isReadOnlyModeEnabled(stores) &&
        !hasSecurityBitSet()
      );
    }
  });

  return {
    apiName,
    apiGetter
  };
};

export const freeTextApis = stores => {
  const apiName = apiNames.ENABLE_FREETEXT;
  const apiGetter = () => ({
    isFreetextEnabled: () => stores.documentStore.enableFreetext
  });

  return {
    apiName,
    apiGetter
  };
};

export const integrationContextApis = (stores, config) => {
  const apiName = apiNames.IS_INTEGRATION_CONTEXT;
  const apiGetter = () => ({
    isIntegrationContext: () => isIntegrationContext(config.context)
  });

  return {
    apiName,
    apiGetter
  };
};

export const commandManagerApis = () => {
  const apiName = apiNames.COMMAND_MANAGER;
  const apiGetter = () => ({
    executeCommand: command => {
      commandManager.executeCommand(command);
    },
    removeLastEntryFromUndoStack: () => {
      commandManager.removeLastEntryFromUndoStack();
    },
    registerPreUndoHandler: fn => {
      commandManager.registerPreUndoHandler(fn);
    },
    undo: opts => {
      commandManager.undo(opts);
    },
    hasUndoCommand: () => commandManager.hasUndoCommand(),
    registerPreRedoHandler: fn => {
      commandManager.registerPreRedoHandler(fn);
    },
    redo: opts => {
      commandManager.redo(opts);
    },
    hasRedoCommand: command => commandManager.hasRedoCommand(),
    reset: () => {
      commandManager.reset();
    },
    clearStack: () => {
      commandManager.clearStack();
    }
  });

  return {
    apiName,
    apiGetter
  };
};
